import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TodayOutlined, CalendarMonthOutlined, ArrowForwardOutlined, MenuBookOutlined, QuizOutlined, EditNoteOutlined, CloseOutlined, AccessTimeOutlined, AddOutlined } from '../../icons';
import { DAYS, formatDuration } from '../StudyPlanCalendar/StudyPlanCalendar';
import { SUBJECT_COLOR_PALETTE, hexToRgba } from '../../utils/subjectColors';
import StudyTimerModal from '../StudyTimer/StudyTimer';
import '../../dashboard/MyMocksPage.css';
import '../../practice/FreeQuestions/FreeQuestions.css';

// getDay(): 0=dom..6=sáb -> índice em DAYS (segunda primeiro), mesma conta
// usada em utils/studyStreak.js pra achar a segunda-feira da semana atual.
const getTodayDay = () => DAYS[(new Date().getDay() + 6) % 7];

// Seção "Estudo do dia" (aba Home, acima da prévia de simulados): traduz o
// Plano de Estudos Personalizado (StudyPlan/StudyPlanCalendar.js) pro dia de
// hoje, pra o aluno ver de cara o que precisa estudar sem entrar no plano
// completo. Reaproveita a mesma paleta por matéria do plano (subjectColors.js).
const TodayStudyPanel = ({ plan }) => {
  const navigate = useNavigate();
  const today = getTodayDay();
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showTimer, setShowTimer] = useState(false);

  if (!plan) {
    return (
      <div className="today-study-panel today-study-panel--empty">
        <div className="edital-progress-header">
          <span className="edital-progress-icon">
            <TodayOutlined style={{ fontSize: 20 }} />
          </span>
          <div>
            <h3>Estudo do dia</h3>
            <span className="edital-progress-subtitle">
              Monte seu plano de estudos personalizado e veja aqui, todo dia, o que estudar.
            </span>
          </div>
        </div>
        <button type="button" className="today-study-practice-cta" onClick={() => navigate('/questao')}>
          <span className="today-study-practice-cta-icon">
            <MenuBookOutlined style={{ fontSize: 22 }} />
          </span>
          <span className="today-study-practice-cta-text">
            <strong>Realizar questões</strong>
            <span>Pratique agora com nosso banco de questões livres</span>
          </span>
          <ArrowForwardOutlined style={{ fontSize: 18 }} />
        </button>
        <button type="button" className="today-study-cta" onClick={() => navigate('/plano-de-estudos')}>
          <CalendarMonthOutlined style={{ fontSize: 18 }} />
          Montar plano de estudos
        </button>
        <button type="button" className="today-study-timer-btn" onClick={() => setShowTimer(true)}>
          <AccessTimeOutlined style={{ fontSize: 16 }} />
          Cronômetro
        </button>
        {showTimer && <StudyTimerModal onClose={() => setShowTimer(false)} />}
      </div>
    );
  }

  const isStudyDay = plan.studyDays.includes(today.key);
  const blocks = plan.schedule[today.key] || [];
  const totalMinutes = blocks.reduce((sum, block) => sum + block.durationMinutes, 0);

  return (
    <div className="today-study-panel">
      <div className="today-study-header-row">
        <div className="edital-progress-header">
          <span className="edital-progress-icon">
            <TodayOutlined style={{ fontSize: 20 }} />
          </span>
          <div>
            <h3>Estudo do dia</h3>
            <span className="edital-progress-subtitle">
              {isStudyDay && blocks.length > 0
                ? `${today.label} · ${formatDuration(totalMinutes)} de estudo planejado`
                : `${today.label} · sem estudo programado hoje`}
            </span>
          </div>
        </div>
        <button type="button" className="today-study-timer-btn" onClick={() => setShowTimer(true)}>
          <AccessTimeOutlined style={{ fontSize: 16 }} />
          Cronômetro
        </button>
      </div>

      <button type="button" className="today-study-practice-cta" onClick={() => navigate('/questao')}>
        <span className="today-study-practice-cta-icon">
          <MenuBookOutlined style={{ fontSize: 20 }} />
        </span>
        <span className="today-study-practice-cta-text">
          <strong>Realizar questões</strong>
        </span>
        <ArrowForwardOutlined style={{ fontSize: 18 }} />
      </button>

      {isStudyDay && blocks.length > 0 ? (
        <div className="today-study-list">
          {blocks.map((block, index) => {
            const subjectIndex = plan.subjects.findIndex((s) => s.subjectId === block.subjectId);
            const pair = SUBJECT_COLOR_PALETTE[(subjectIndex >= 0 ? subjectIndex : 0) % SUBJECT_COLOR_PALETTE.length];
            return (
              <div
                key={index}
                className="today-study-item"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedBlock({ ...block, color: pair.border })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedBlock({ ...block, color: pair.border });
                  }
                }}
                style={{
                  borderLeftColor: pair.border,
                  backgroundColor: pair.bg,
                  '--today-study-item-bg-dark': hexToRgba(pair.border, 0.18)
                }}
              >
                <div className="today-study-item-top">
                  <span className="today-study-item-duration">{formatDuration(block.durationMinutes)}</span>
                </div>
                <span className="today-study-item-subject">{block.subjectName}</span>
                <span className="today-study-item-hint">
                  Clique para ver opções
                  <ArrowForwardOutlined style={{ fontSize: 12 }} />
                </span>
              </div>
            );
          })}

          <div
            className="today-study-item today-study-item-add"
            role="button"
            tabIndex={0}
            onClick={() => setSelectedBlock({ subjectId: null, subjectName: null, durationMinutes: null })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedBlock({ subjectId: null, subjectName: null, durationMinutes: null });
              }
            }}
          >
            <span className="today-study-item-add-icon">
              <AddOutlined style={{ fontSize: 20 }} />
            </span>
            <span className="today-study-item-add-label">Estudar outro tópico</span>
          </div>
        </div>
      ) : (
        <div className="today-study-rest">
          {isStudyDay
            ? 'Nenhum bloco de estudo programado para hoje.'
            : 'Hoje é seu dia de descanso no plano. Aproveite para revisar seus erros ou recarregar as energias.'}
        </div>
      )}

      <button type="button" className="today-study-link" onClick={() => navigate('/plano-de-estudos')}>
        Ver plano completo
        <ArrowForwardOutlined style={{ fontSize: 14 }} />
      </button>

      {selectedBlock && (
        <div className="modal-backdrop" onClick={() => setSelectedBlock(null)}>
          <div className="modal-card today-study-modal-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              onClick={() => setSelectedBlock(null)}
              aria-label="Fechar"
            >
              <CloseOutlined style={{ fontSize: 18 }} />
            </button>
            <div className="today-study-modal-header">
              <span
                className="today-study-modal-bar"
                style={selectedBlock.color ? { background: selectedBlock.color } : undefined}
              />
              <div className="today-study-modal-heading">
                <strong>{selectedBlock.subjectName || 'Novo estudo'}</strong>
                <span>O que você quer fazer agora?</span>
              </div>
            </div>
            <div className="modal-actions today-study-modal-actions">
              <button
                type="button"
                className="today-study-modal-option today-study-modal-option--primary"
                onClick={() => navigate('/questao', { state: { subjectId: selectedBlock.subjectId } })}
              >
                <span className="today-study-modal-option-icon">
                  <QuizOutlined style={{ fontSize: 18 }} />
                </span>
                <span className="today-study-modal-option-text">
                  <strong>Realizar questões</strong>
                  <span>continuar de onde parou</span>
                </span>
              </button>
              <button
                type="button"
                className="today-study-modal-option today-study-modal-option--timer"
                onClick={() => {
                  setSelectedBlock(null);
                  setShowTimer(true);
                }}
              >
                <span className="today-study-modal-option-icon">
                  <AccessTimeOutlined style={{ fontSize: 18 }} />
                </span>
                <span className="today-study-modal-option-text">
                  <strong>Cronometrar estudo</strong>
                  <span>Pomodoro ou tempo livre</span>
                </span>
              </button>
              <button
                type="button"
                className="today-study-modal-option today-study-modal-option--secondary"
                onClick={() => navigate('/diario-de-estudos', { state: { subjectId: selectedBlock.subjectId } })}
              >
                <span className="today-study-modal-option-icon">
                  <EditNoteOutlined style={{ fontSize: 18 }} />
                </span>
                <span className="today-study-modal-option-text">
                  <strong>Registrar estudo</strong>
                  <span>anotar sessão já feita</span>
                </span>
              </button>
            </div>
            <span className="today-study-modal-cancel" onClick={() => setSelectedBlock(null)}>
              Cancelar
            </span>
          </div>
        </div>
      )}

      {showTimer && <StudyTimerModal onClose={() => setShowTimer(false)} />}
    </div>
  );
};

export default TodayStudyPanel;
